import { useState, useEffect } from 'react';
import { useEditor } from '../contexts/EditorContext';
import {  
  NavigationOperationPayload,
  getPageCanvas
} from '../services/NavigationApiService';
import { mockApiService } from '../services/MockApiService';
import { generateComponentId } from '../utils/IdGenerator';
import { Container as ContainerType } from '../types/canvas.types';
import { useUIModelApi } from '../contexts/UIModelApiContext';

/**
 * Hook for managing navigation operations (groups and pages)
 * Provides functions for creating, updating, and deleting groups and pages
 * with optimistic updates for a responsive user experience
 */
export const useNavigationOperations = (modelId: string) => {
  const { groups, setGroups, editorState, setEditorState, containers, setContainers } = useEditor();
  const { setCanvasMap, setCanvas, markPageAsModified } = useUIModelApi();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentModelId, setCurrentModelId] = useState<string | null>(null);
  

  useEffect(() => {
    if (!modelId) {
      const id = mockApiService.getCurrentModelId();
      setCurrentModelId(id);
    } else {
      setCurrentModelId(modelId);
    }
  }, [modelId]);

  /**
   * Performs a navigation operation with optimistic updates
   */
  const performOperation = async (payload: NavigationOperationPayload) => {
    setIsLoading(true);
    setError(null);
    
    const effectiveModelId = modelId || currentModelId;
    
    if (!effectiveModelId) {
      setError('No model ID available');
      setIsLoading(false);
      return { success: false, error: 'No model ID available' };
    }
    
    try {
      let optimisticGroups;
      
      switch (payload.operation) {
        case 'create': {
          if (payload.target === 'group') {
            const newGroup = {
              id: payload.id || generateComponentId('group'),
              name: payload.data?.name || 'New Group',
              order: groups.length,
              orientation: payload.data?.orientation || 'horizontal',
              configuration: {
                showTitle: payload.data?.showTitle !== undefined ? payload.data.showTitle : true,
              },
              pages: []
            };
            
            optimisticGroups = [...groups, newGroup];
            setGroups(optimisticGroups);
          } else if (payload.target === 'page' && payload.parentId) {
            optimisticGroups = groups.map(group => {
              if (group.id === payload.parentId) {
                const newPage = {
                  id: payload.id || generateComponentId('page'),
                  name: payload.data?.title || 'New Page',
                  order: group.pages.length,
                  configuration: {
                    autoAdvance: payload.data?.autoAdvance !== undefined ? payload.data.autoAdvance : false,
                  },
                  canvas: { containers: [] }
                };
                
                return {
                  ...group,
                  pages: [...group.pages, newPage]
                };
              }
              return group;
            });
            
            setGroups(optimisticGroups);
          }
          break;
        }
        
        case 'update': {
          if (payload.target === 'group' && payload.id) {
            optimisticGroups = groups.map(group => {
              if (group.id === payload.id) {
                return {
                  ...group,
                  name: payload.data?.name !== undefined ? payload.data.name : group.name,
                  orientation: payload.data?.orientation !== undefined ? payload.data.orientation : group.orientation,
                  configuration: {
                    ...group.configuration,
                    showTitle: payload.data?.showTitle !== undefined 
                      ? payload.data.showTitle 
                      : group.configuration.showTitle,
                  }
                };
              }
              return group;
            });
            
            setGroups(optimisticGroups);
          } else if (payload.target === 'page' && payload.id && payload.parentId) {
            optimisticGroups = groups.map(group => {
              if (group.id === payload.parentId) {
                return {
                  ...group,
                  pages: group.pages.map(page => {
                    if (page.id === payload.id) {
                      return {
                        ...page,
                        name: payload.data?.title !== undefined ? payload.data.title : page.name,
                        configuration: {
                          ...page.configuration,
                          autoAdvance: payload.data?.autoAdvance !== undefined
                            ? payload.data.autoAdvance
                            : page.configuration.autoAdvance,
                        }
                      };
                    }
                    return page;
                  })
                };
              }
              return group;
            });
            
            setGroups(optimisticGroups);
          }
          break;
        }
        
        case 'delete': {
          if (payload.target === 'group' && payload.id) {
            optimisticGroups = groups.filter(group => group.id !== payload.id);
            
            if (editorState.activeGroupId === payload.id && optimisticGroups.length > 0) {
              const newActiveGroup = optimisticGroups[0];
              const newActiveTab = newActiveGroup.pages.length > 0 ? newActiveGroup.pages[0].id : null;
              
              setEditorState(prev => ({
                ...prev,
                activeGroupId: newActiveGroup.id,
                activePageId: newActiveTab
              }));
            }
            
            setGroups(optimisticGroups);
          } else if (payload.target === 'page' && payload.id && payload.parentId) {
            optimisticGroups = groups.map(group => {
              if (group.id === payload.parentId) {
                const updatedTabs = group.pages.filter(page => page.id !== payload.id);
                
                if (editorState.activePageId === payload.id && 
                    editorState.activeGroupId === payload.parentId &&
                    updatedTabs.length > 0) {
                  setEditorState(prev => ({
                    ...prev,
                    activePageId: updatedTabs[0].id
                  }));
                }
                
                return {
                  ...group,
                  pages: updatedTabs
                };
              }
              return group;
            });
            
            setGroups(optimisticGroups);
          }
          break;
        }
      }
    
      return { success: true };
    } catch (err) {
      console.error('Navigation operation error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return { success: false, error: err instanceof Error ? err.message : 'Operation failed' };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new group with the given configuration
   */
  const createGroup = async (name: string, config?: any) => {
    return performOperation({
      operation: 'create',
      target: 'group',
      data: {
        name,
        ...config
      }
    });
  };

  /**
   * Update an existing group with new configuration
   */
  const updateGroup = async (groupId: string, config: any) => {
    return performOperation({
      operation: 'update',
      target: 'group',
      id: groupId,
      data: config
    });
  };

  /**
   * Delete a group by ID
   */
  const deleteGroup = async (groupId: string) => {
    return performOperation({
      operation: 'delete',
      target: 'group',
      id: groupId
    });
  };

  /**
   * Create a new page within a group
   */
  const createPage = async (groupId: string, title: string, config?: any) => {
    return performOperation({
      operation: 'create',
      target: 'page',
      parentId: groupId,
      data: {
        title,
        ...config
      }
    });
  };

  /**
   * Update an existing page with new configuration
   */
  const updatePage = async (pageId: string, groupId: string, config: any) => {
    return performOperation({
      operation: 'update',
      target: 'page',
      id: pageId,
      parentId: groupId,
      data: config
    });
  };

  /**
   * Delete a page by ID
   */
  const deletePage = async (pageId: string, groupId: string) => {
    return performOperation({
      operation: 'delete',
      target: 'page',
      id: pageId,
      parentId: groupId
    });
  };

  /**
   * Fetch canvas data for a specific page
   */
  const fetchPageCanvas = async (groupId: string, pageId: string) => {
    setIsLoading(true);
    setError(null);
    
    const effectiveModelId = modelId || currentModelId;
    
    if (!effectiveModelId) {
      setError('No model ID available');
      setIsLoading(false);
      return { success: false, error: 'No model ID available' };
    }
    
    try {
      const result = await getPageCanvas(effectiveModelId, groupId, pageId);
      if (!result.success) {
        setError(result.error || 'Failed to fetch page canvas');
      } else {
        setError(null);
      }
      return result;
    } catch (err) {
      console.error("Error fetching page canvas:", err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return { success: false, error: 'Failed to fetch page canvas' };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Deep clone container structure without content
   */
  const cloneContainerStructure = (
    source: ContainerType,
    parentId?: string,
    isNested: boolean = false,
    nameMapping?: Map<string, string>
  ): ContainerType => {
    const newId = generateComponentId(isNested ? 'nested-container' : 'container');
    
    if (nameMapping && source.name) {
      nameMapping.set(source.name, source.name);
    }
    

    let clonedNestedContainers: ContainerType[] = [];
    if (source.content.nestedContainers && source.content.nestedContainers.length > 0) {
      clonedNestedContainers = source.content.nestedContainers.map(nested => {
        return cloneContainerStructure(
          nested,
          newId,
          true,
          nameMapping
        );
      });
    }
    
    const clonedContainer: ContainerType = {
      id: newId,
      name: source.name,
      order: source.order,
      configuration: {
        ...source.configuration
      },
      metadata: source.metadata ? {
        ...source.metadata,
        parentContainerId: parentId
      } : undefined,
      content: {
        type: 'widget',
        componentId: 'empty',
        'widget-type': 'contentBlock',
        nestedContainers: clonedNestedContainers,
        items: [],
        submodel: undefined
      },
      usageInfo: {
        usedComponents: []
      }
    };
    
    return clonedContainer;
  };

  /**
   * Duplicate a page with all its containers (structure and config only, no content)
   */
  const duplicatePage = async (pageId: string, groupId: string, pageName: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      const pageIndex = group.pages.findIndex(p => p.id === pageId);
      if (pageIndex === -1) {
        throw new Error('Page not found');
      }
      
      const originalPage = group.pages[pageIndex];
      
      const newPageId = generateComponentId('page');
      const newCanvasId = `canvas-${newPageId}`;
      const newPageName = `${pageName} (Copy)`;
      
      const duplicatedPage = {
        id: newPageId,
        name: newPageName,
        order: pageIndex + 1,
        configuration: {
          ...originalPage.configuration
        },
        canvas: { containers: [] }
      };
      
      const updatedPages = [...group.pages];
      for (let i = pageIndex + 1; i < updatedPages.length; i++) {
        updatedPages[i] = { ...updatedPages[i], order: updatedPages[i].order + 1 };
      }
      
      updatedPages.splice(pageIndex + 1, 0, duplicatedPage);
      
      const updatedGroups = groups.map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            pages: updatedPages
          };
        }
        return g;
      });
      
      setGroups(updatedGroups);
      
      setCanvasMap(prev => ({
        ...prev,
        [newPageId]: newCanvasId
      }));
      
      const originalContainers = containers[pageId];
      if (originalContainers && originalContainers.length > 0) {
        const clonedContainers = originalContainers.map((container, index) => {
          const cloned = cloneContainerStructure(container, undefined, false);
          cloned.order = container.order;
          if (cloned.metadata) {
            cloned.metadata.rowIndex = container.metadata?.rowIndex || 0;
          }
          return cloned;
        });
        
        setContainers(prev => ({
          ...prev,
          [newPageId]: clonedContainers
        }));
        
        const canvasData = {
          pageId: newPageId,
          groupId: groupId,
          containers: []
        };
        
        setCanvas(prev => ({
          ...prev,
          [newCanvasId]: canvasData
        }));
      } else {
        setContainers(prev => ({
          ...prev,
          [newPageId]: []
        }));
        
        const canvasData = {
          pageId: newPageId,
          groupId: groupId,
          containers: []
        };
        
        setCanvas(prev => ({
          ...prev,
          [newCanvasId]: canvasData
        }));
      }
      
      markPageAsModified(newPageId);
      
      setEditorState(prev => ({
        ...prev,
        activePageId: newPageId
      }));
      
      return { success: true, newPageId };
    } catch (err) {
      console.error('Duplicate page error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to duplicate page' };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    createGroup,
    updateGroup,
    deleteGroup,
    createPage,
    updatePage,
    deletePage,
    duplicatePage,
    fetchPageCanvas
  };
};